/**
 * BotSpot Web Dashboard Credentials API
 *
 * API functions for secure credential management.
 */

import { apiClient } from "./client";
import type {
  Credential,
  CredentialCreate,
  CredentialUpdate,
  CredentialList,
  MessageResponse,
  PaginationParams,
} from "./types";

/**
 * List all credentials for the current user
 *
 * @param params - Pagination parameters
 * @returns List of credentials (metadata only)
 */
export async function listCredentials(
  params?: PaginationParams
): Promise<CredentialList> {
  const queryParams = new URLSearchParams();
  if (params?.page !== undefined) {
    queryParams.append("page", params.page.toString());
  }
  if (params?.page_size !== undefined) {
    queryParams.append("page_size", params.page_size.toString());
  }

  const url = `/credentials${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  return await apiClient.get<CredentialList>(url);
}

/**
 * Get details of a specific credential
 *
 * @param credentialId - Credential ID
 * @returns Credential metadata
 */
export async function getCredential(credentialId: string): Promise<Credential> {
  return await apiClient.get<Credential>(`/credentials/${credentialId}`);
}

/**
 * Create a new credential
 *
 * @param credentialData - Credential creation data
 * @returns Created credential object (metadata only)
 */
export async function createCredential(
  credentialData: CredentialCreate
): Promise<Credential> {
  return await apiClient.post<Credential>("/credentials", credentialData);
}

/**
 * Update an existing credential
 *
 * @param credentialId - Credential ID
 * @param updates - Credential update data
 * @returns Updated credential object (metadata only)
 */
export async function updateCredential(
  credentialId: string,
  updates: CredentialUpdate
): Promise<Credential> {
  return await apiClient.patch<Credential>(
    `/credentials/${credentialId}`,
    updates
  );
}

/**
 * Delete a credential
 *
 * @param credentialId - Credential ID
 * @returns Success message
 */
export async function deleteCredential(
  credentialId: string
): Promise<MessageResponse> {
  return await apiClient.delete<MessageResponse>(
    `/credentials/${credentialId}`
  );
}
